package com.evoapps.evoshift.data.repository

import com.evoapps.evoshift.data.remote.AppFirestore
import com.evoapps.evoshift.domain.repository.StartPathRepository
import com.evoapps.evoshift.domain.usecase.start.StartPathProgress
import com.evoapps.evoshift.domain.usecase.start.StartStep
import com.evoapps.evoshift.domain.util.AppClock
import kotlinx.serialization.Serializable

/**
 * `workplaces/{id}/start_path/state` (SH-219): the owner's start path. Skipped steps by their
 * [StartStep.id] (unknown ids from a newer app are kept as they are read: ignored, not an error).
 * firestore.rules (validStartPath) accept exactly these fields.
 */
@Serializable
internal data class StartPathDto(
    val skipped: List<String> = emptyList(),
    val dismissed: Boolean = false,
    val startedAt: String? = null,
    val firstPublishedAt: String? = null,
    val updatedAt: String? = null
)

internal fun StartPathDto.toDomain() = StartPathProgress(
    skipped = skipped.mapNotNull(StartStep::of).toSet(),
    dismissed = dismissed,
    startedAt = startedAt,
    firstPublishedAt = firstPublishedAt
)

internal fun StartPathProgress.toDto(updatedAt: String) = StartPathDto(
    skipped = StartStep.entries.filter { it in skipped }.map { it.id },
    dismissed = dismissed,
    startedAt = startedAt,
    firstPublishedAt = firstPublishedAt,
    updatedAt = updatedAt
)

class StartPathRepositoryImpl(private val firestore: AppFirestore) : StartPathRepository {
    private fun document(workplaceId: String) =
        firestore.collection("workplaces").document(workplaceId).collection("start_path").document("state")

    override suspend fun get(workplaceId: String): Result<StartPathProgress?> = runCatching {
        val snapshot = document(workplaceId).get()
        if (!snapshot.exists) null else snapshot.data<StartPathDto>().toDomain()
    }

    override suspend fun save(workplaceId: String, progress: StartPathProgress): Result<Unit> = runCatching {
        document(workplaceId).set(progress.toDto(AppClock.now().toString()))
    }
}
